// src/components/Pagination.jsx
import React, { useMemo } from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';

export default function Pagination({ page, setPage, limit, setLimit, total }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 1)));

  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);

  const goFirst = () => setPage(1);
  const goPrev = () => setPage(p => Math.max(1, p - 1));
  const goNext = () => setPage(p => Math.min(totalPages, p + 1));
  const goLast = () => setPage(totalPages);

  const pageNumbers = useMemo(() => {
    // window of up to 5 pages around current
    const span = 2;
    const start = Math.max(1, page - span);
    const end = Math.min(totalPages, page + span);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-3 py-4">
      <div className="text-sm text-gray-600">
        Showing <span className="font-medium">{from}</span>–<span className="font-medium">{to}</span> of{' '}
        <span className="font-medium">{total}</span>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-600">Rows per page:</label>
        <select
          value={limit}
          onChange={e => { setPage(1); setLimit(Number(e.target.value)); }}
          className="border rounded-md p-1.5 text-sm"
        >
          {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>

        <div className="flex items-center gap-1">
          <button
            onClick={goFirst}
            disabled={page === 1}
            className="p-2 rounded border disabled:opacity-40"
            aria-label="First page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goPrev}
            disabled={page === 1}
            className="p-2 rounded border disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {pageNumbers.map(n => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={`px-3 py-1.5 rounded border text-sm ${n === page ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white'}`}
            >
              {n}
            </button>
          ))}

          <button
            onClick={goNext}
            disabled={page === totalPages}
            className="p-2 rounded border disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={goLast}
            disabled={page === totalPages}
            className="p-2 rounded border disabled:opacity-40"
            aria-label="Last page"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
